import React, { useState, useEffect, useRef } from 'react'
import { TrendingUp, TestTube, Beaker, Microscope, Download } from 'lucide-react'
import { SimulationAPI } from '../lib/api'
import { useLocale } from '../i18n/LocaleContext'
import type { MessageKey } from '../i18n/locales'

interface NovelSubstance {
  id: string
  timestamp: number  
  size: number
  complexity: number
  properties: {
    mass: number
    charge: [number, number, number]
    bonds: number
    graph_density: number
  }
}

interface NovelSubstanceProps {
  substance: NovelSubstance
  onSelect?: (id: string) => void
  onSave?: (id: string) => void
}

const NovelSubstanceCard: React.FC<NovelSubstanceProps> = ({ substance, onSelect, onSave }) => {
  const { t, dateLocale } = useLocale()
  const timeStr = new Date(substance.timestamp * 1000).toLocaleTimeString(dateLocale)
  
  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSave?.(substance.id)
  }
  
  return (
    <div 
      className="bg-gray-800 bg-opacity-50 p-3 rounded border border-gray-600 hover:border-blue-400 cursor-pointer transition-colors"
      onClick={() => onSelect?.(substance.id)}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Beaker className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-blue-300">{substance.id.slice(-8)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{timeStr}</span>
          <button
            onClick={handleSaveClick}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
            title={t('novelty.save.title')}
          >
            <Download className="w-3 h-3 text-green-400" />
          </button>
        </div>
      </div>
      
      <div className="space-y-1 text-xs text-gray-300">
        <div className="flex justify-between">
          <span>{t('novelty.field.size')}</span>
          <span className="text-blue-300">{t('novelty.atoms', { n: substance.size })}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('novelty.field.complexity')}</span>
          <span className="text-green-300">{substance.complexity.toFixed(3)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('novelty.field.bonds')}</span>
          <span className="text-yellow-300">{substance.properties.bonds}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('novelty.field.density')}</span>
          <span className="text-purple-300">{substance.properties.graph_density.toFixed(3)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t('novelty.field.avgMass')}</span>
          <span className="text-orange-300">{substance.properties.mass.toFixed(2)}</span>
        </div>
      </div>
    </div>
  )
}

interface NoveltyPanelProps {
  simulationId: string | null
  onSubstanceSelect?: (id: string) => void
}

const getNoveltyMessage = (rate: number, t: (key: MessageKey) => string) => {
  if (rate > 0.3) return t('novelty.level.high')
  if (rate > 0.1) return t('novelty.level.medium')
  if (rate > 0.01) return t('novelty.level.low')
  return t('novelty.level.dormant')
}

const NoveltyPanel: React.FC<NoveltyPanelProps> = ({ simulationId, onSubstanceSelect }) => {
  const { t } = useLocale()
  const [novelSubstances, setNovelSubstances] = useState<NovelSubstance[]>([])
  const [noveltyRate, setNoveltyRate] = useState(0)
  const [totalNovel, setTotalNovel] = useState(0)
  const [totalDiscovered, setTotalDiscovered] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isMatching, setIsMatching] = useState(false)

  const api = useRef(new SimulationAPI()).current

  useEffect(() => {
    if (!simulationId) {
      console.log('NoveltyPanel: No simulationId, skipping update')
      return
    }

    const updateNovelty = async () => {
      console.log('NoveltyPanel: Updating novelty data for simulation', simulationId)
      setLoading(true)
      try {
        // Get novel substances
        const novelResponse = await api.getNovelSubstances(simulationId, 20)
        console.log('NoveltyPanel: Received', novelResponse.substances?.length || 0, 'substances')
        // BUGFIX: Deduplicate substances by ID to prevent React key warnings
        const substances = novelResponse.substances || []
        const uniqueSubstances = substances.filter((substance: NovelSubstance, index: number, self: NovelSubstance[]) => 
          index === self.findIndex((s: NovelSubstance) => s.id === substance.id)
        )
        
        // FLICKER FIX: Only update if there are actual changes
        setNovelSubstances(prev => {
          // If lengths differ, update
          if (prev.length !== uniqueSubstances.length) {
            return uniqueSubstances
          }
          
          // Check if any IDs are different
          const prevIds = new Set(prev.map((s: NovelSubstance) => s.id))
          const newIds = new Set(uniqueSubstances.map((s: NovelSubstance) => s.id))
          const hasChanges = prev.some((s: NovelSubstance) => !newIds.has(s.id)) || 
                            uniqueSubstances.some((s: NovelSubstance) => !prevIds.has(s.id))
          
          // Only update if there are actual changes
          return hasChanges ? uniqueSubstances : prev
        })

        // Get metrics for novelty rate
        const metricsResponse = await api.getMetrics(simulationId)
        const metrics = metricsResponse.metrics
        
        setNoveltyRate(metrics.novelty_rate || 0)
        
        // Catalog stats are directly in metrics (not nested)
        setTotalNovel(metrics.total_novel || 0)
        setTotalDiscovered(metrics.total_discovered || 0)
      } catch (error: any) {
        // Stop polling if simulation doesn't exist
        if (error?.message?.includes('404') || error?.message?.includes('not found')) {
          console.warn('Simulation not found, stopping novelty updates')
          return
        }
        console.error('Failed to update novelty data:', error)
      } finally {
        setLoading(false)
      }
    }

    updateNovelty()
    
    // Update every 15 seconds (reduced frequency to minimize flicker)
    const interval = setInterval(updateNovelty, 15000)
    return () => clearInterval(interval)
  }, [simulationId, api])

  const handleMatchAllClusters = async () => {
    if (!simulationId || novelSubstances.length === 0) return
    
    setIsMatching(true)
    try {
      console.log(`🔍 Matching ${novelSubstances.length} clusters with PubChem...`)
      
      let successCount = 0
      for (const substance of novelSubstances) {
        try {
          await api.matchSubstanceToPubchem(simulationId, substance.id)
          successCount++
        } catch (error) {
          console.error(`Failed to match cluster ${substance.id}:`, error)
        }
      }
      
      alert(t('novelty.alert.matchAll', { ok: successCount, total: novelSubstances.length }))
    } catch (error) {
      console.error('Failed to match clusters:', error)
      alert(t('novelty.alert.matchAllFail', { error: String(error) }))
    } finally {
      setIsMatching(false)
    }
  }

  const handleSaveCluster = async (substanceId: string) => {
    if (!simulationId) return
    
    try {
      console.log(`🔍 Matching cluster ${substanceId} to PubChem...`)
      
      // Call backend API to match substance
      const result = await api.matchSubstanceToPubchem(simulationId, substanceId)
      
      if (result.success) {
        console.log('✅ Match completed!')
        console.log(`SMILES: ${result.smiles}`)
        
        if (result.pubchem_match) {
          console.log(`🎯 PubChem Match: CID ${result.pubchem_match.cid}`)
          console.log(`   Name: ${result.pubchem_match.name}`)
          console.log(`   Formula: ${result.pubchem_match.formula}`)
        } else {
          console.log('ℹ️  No PubChem match found')
        }
        
        console.log('\n📁 Generated files:')
        Object.entries(result.files).forEach(([key, path]) => {
          console.log(`   ${key}: ${path}`)
        })
        
        // Show success message to user
        const matchInfo = result.pubchem_match 
          ? t('novelty.alert.matchOk', { name: result.pubchem_match.name, cid: result.pubchem_match.cid })
          : t('novelty.alert.noMatch')
        
        alert(`${matchInfo}\n\nSMILES: ${result.smiles}`)
      }
      
    } catch (error) {
      console.error('❌ Failed to match cluster:', error)
      alert(t('novelty.alert.matchFail', { error: String(error) }))
    }
  }

  const getNoveltyColor = (rate: number) => {
    if (rate > 0.3) return 'text-red-400'
    if (rate > 0.1) return 'text-yellow-400'
    return 'text-green-400'
  }

  return (
    <div className="novelty-panel space-y-4 border-2 border-purple-500 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-purple-400" />
        <h3 className="text-lg font-semibold text-white">{t('novelty.title')}</h3>
      </div>

      {/* Novelty Stats */}
      <div className="bg-gray-800 bg-opacity-40 p-4 rounded border border-purple-500 border-opacity-30">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">{t('novelty.emergenceRate')}</span>
            <span className={`text-lg font-bold ${getNoveltyColor(noveltyRate)}`}>
              {" "}({noveltyRate.toFixed(4)})
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Microscope className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-gray-300">{getNoveltyMessage(noveltyRate, t)}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="text-center">
              <div className="text-blue-300 font-semibold">{totalNovel}</div>
              <div className="text-gray-400">{t('novelty.stat.novel')}</div>
            </div>
            <div className="text-center">
              <div className="text-green-300 font-semibold">{totalDiscovered}</div>
              <div className="text-gray-400">{t('novelty.stat.discovered')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Discoveries */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-semibold text-white flex items-center gap-2">
            <TestTube className="w-4 h-4 text-green-400" />
            {t('novelty.discoveries')}
          </h4>
          
          {/* Main PubChem Matcher Button - ALWAYS VISIBLE */}
          <div className="flex gap-2">
            <button
              onClick={handleMatchAllClusters}
              disabled={isMatching}
              className="px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs font-semibold rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title={t('novelty.matchAll.title', { n: novelSubstances.length })}
            >
              {isMatching ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span>{t('novelty.matchAll.loading')}</span>
                </>
              ) : (
                <>
                  <Microscope className="w-3 h-3" />
                  <span>{t('novelty.matchAll.label', { n: novelSubstances.length })}</span>
                </>
              )}
            </button>
            
            {/* Download Button - ALWAYS VISIBLE */}
            <button
              onClick={() => {
                // Export novel substances to JSON
                const dataStr = JSON.stringify(novelSubstances, null, 2)
                const dataBlob = new Blob([dataStr], {type: 'application/json'})
                const url = URL.createObjectURL(dataBlob)
                const link = document.createElement('a')
                link.href = url
                link.download = `novel_substances_${Date.now()}.json`
                link.click()
                URL.revokeObjectURL(url)
              }}
              className="px-2 py-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-xs font-semibold rounded-lg shadow-lg transition-all flex items-center gap-1"
              title={t('novelty.download.title')}
            >
              <Download className="w-3 h-3" />
              <span>🔽</span>
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
          </div>
        ) : novelSubstances.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {novelSubstances.map((substance) => (
              <NovelSubstanceCard 
                key={substance.id}
                substance={substance}
                onSelect={onSubstanceSelect}
                onSave={handleSaveCluster}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 bg-gray-800 bg-opacity-30 rounded border border-gray-600">
            <TestTube className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="font-semibold text-yellow-400">{t('novelty.empty.title')}</p>
            <p className="text-xs mt-2">{t('novelty.empty.none')}</p>
            <p className="text-xs mt-1">{t('novelty.empty.hint')}</p>
            <p className="text-xs mt-1 text-blue-400">{t('novelty.empty.bonds')}</p>
            <p className="text-xs mt-2 text-purple-400">{t('novelty.empty.cta')}</p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="text-xs text-gray-400 bg-gray-800 bg-opacity-30 p-3 rounded">
        <p className="font-medium text-gray-300 mb-1">{t('novelty.about.title')}</p>
        <p>{t('novelty.about.body')}</p>
      </div>
    </div>
  )
}

export default NoveltyPanel
